import React, { useMemo } from 'react';
import ActivityTab from 'v2/common/DRM/ActivityTab/ActivityTab';
import FilesTab from 'v2/common/DRM/FilesTab/FilesTab';
import CustomTabComponent from 'v2/common/DRM/CustomTab';
import DataProductDetailTab from 'v2/common/DRM/DataProduct/DataProductDetailTab/DataProductDetailTab';
import DataProductRestrictions from 'v2/common/DRM/DataProduct/DataProductRestrictions/DataProductRestrictions';
import {
  ActivityAssociation,
  DataVendor,
  DrmPage,
  DjangoListResponse,
  CustomTab,
  DataProduct,
  DataProductTabType,
  DrmEntityBackend,
} from 'v2/models';
import { mapContactAssociations } from 'v2/utils/activityUtils';
import useGetContacts from 'v2/hooks/useGetContacts';
import GenericAssociationTab, {
  DRMEntity,
} from 'v2/common/DRM/GenericAssociations/GenericAssociationTab';
import { useDataProduct } from 'v2/queries/useDataProduct';

interface DataProductTabContentProps {
  dataVendor: DataVendor | undefined;
  selectedTab: string;
  customTabs: DjangoListResponse<CustomTab> | undefined;
  TABS: DataProductTabType[];
  dataProduct: DataProduct;
  onUpdate: () => void;
}

const DataProductTabContent = ({
  selectedTab,
  dataVendor,
  dataProduct: localDataProduct,
  customTabs,
  TABS,
  onUpdate,
}: DataProductTabContentProps) => {
  const { data: dataProduct } = useDataProduct({
    id: localDataProduct.id,
  });
  const { data: defaultDataProductContacts } = useGetContacts(
    {
      data_product: dataProduct?.id,
    },
    {},
    0
  );
  const { data: defaultDataVendorContacts } = useGetContacts(
    {
      data_vendor: dataVendor?.id,
    },
    {},
    0
  );

  const contactAssociations = useMemo<ActivityAssociation[]>(() => {
    const vendorContacts = mapContactAssociations(defaultDataVendorContacts);
    const productContacts = mapContactAssociations(defaultDataProductContacts);

    return [...vendorContacts, ...productContacts];
  }, [defaultDataVendorContacts, defaultDataProductContacts]);

  if (!dataProduct) {
    return null;
  }

  const defaultDataVendorAssociation = dataVendor
    ? [
        {
          table_name: 'DataVendor',
          name: dataVendor.name,
          object_id: dataVendor.id,
          selected: false,
          editable: true,
        },
      ]
    : [];

  const defaultDataProductAssociation = dataProduct
    ? [
        {
          table_name: 'DataProduct',
          name: dataProduct.name,
          object_id: dataProduct.id,
          is_mandatory: true,
        },
      ]
    : [];

  const defaultAssociations: ActivityAssociation[] = [
    ...defaultDataVendorAssociation,
    ...defaultDataProductAssociation,
  ];

  const renderTab = () => {
    switch (selectedTab) {
      case 'details':
        return <DataProductDetailTab dataProduct={dataProduct} />;
      case 'associations': {
        const dataProductEntity: DRMEntity = {
          ...dataProduct,
          type: DrmEntityBackend.DATASET,
        };
        return (
          <GenericAssociationTab
            entity={dataProductEntity}
            refetch={onUpdate}
          />
        );
      }

      case 'restrictions':
        return <DataProductRestrictions dataProduct={dataProduct} />;
      case 'activity':
        return (
          <ActivityTab
            relatedTable="DataProduct"
            relatedObjectId={dataProduct.id}
            defaultAssociations={[
              ...defaultAssociations,
              ...contactAssociations,
            ]}
          />
        );
      case 'files':
        return (
          <FilesTab
            relatedTable="DataProduct"
            relatedObjectId={dataProduct.id}
            dataProductId={dataProduct.id}
            primaryObjectType="DataProduct"
            defaultAssociations={[
              ...defaultAssociations,
              ...contactAssociations,
            ]}
            {...(dataVendor && { dataVendorId: dataVendor.id })}
          />
        );
      default:
        if (!TABS.some((tab) => tab['eventKey'] === selectedTab)) {
          return (
            <CustomTabComponent
              drmPage={DrmPage.DATASET}
              tab={customTabs?.results.find((tab) => tab.name === selectedTab)}
              entity={dataProduct}
              onUpdate={onUpdate}
            />
          );
        }
        return null;
    }
  };

  return <div>{renderTab()}</div>;
};

export default DataProductTabContent;
